import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button, Spin, Alert, Tooltip } from 'antd'
import { RobotOutlined, BarChartOutlined, LineChartOutlined, AppstoreOutlined, GroupOutlined, PieChartOutlined, TableOutlined } from '@ant-design/icons'
import { useReportBuilderOptions } from '../../../../hooks/useReports'
import {
  usePracticeAgentWorkspaceInit,
  usePracticeAgentReportData,
  usePracticeAgentWorkspaceInsight,
  usePracticeAgentExecuteDrilldownMutation,
} from '../../hooks'
import type { PracticeAgentReportData, PracticeAgentVisualizationType, PracticeAgentDrilldownData } from '../../types'
import { classifyColumns } from '../../utils'
import { ReportVisualization } from './ReportVisualization'
import { PaginationBar } from './PaginationBar'
import { SortControls } from './SortControls'
import { InsightCard } from './InsightCard'
import { DimensionPickerModal } from './DimensionPickerModal'
import { formatFieldDisplayName } from '../../utils'

const VIZ_SWITCHER: Array<{ key: PracticeAgentVisualizationType; icon: React.ReactNode; label: string }> = [
  { key: 'Bar', icon: <BarChartOutlined />, label: 'Bar' },
  { key: 'Donut', icon: <PieChartOutlined />, label: 'Donut' },
  { key: 'GroupedBar', icon: <GroupOutlined />, label: 'Grouped' },
  { key: 'Line', icon: <LineChartOutlined />, label: 'Line' },
  { key: 'KPI', icon: <AppstoreOutlined />, label: 'KPI' },
  { key: 'Table', icon: <TableOutlined />, label: 'Table' },
]

export function ReportPanel({
  clientId,
  reportBuilderId,
  reportBuilderGroupId,
  initialDrilldown,
  groupReportData,
  drilldownDimensions,
  defaultDrilldownDimension,
  showInsight = true,
  preferredVizType = null,
  mode = 'workspace',
}: {
  clientId: string
  reportBuilderId: string
  reportBuilderGroupId?: string | null
  initialDrilldown?: { filters: Record<string, string>; versionId?: string; dimension?: string } | null
  groupReportData?: PracticeAgentReportData | null
  drilldownDimensions?: string[]
  defaultDrilldownDimension?: string | null
  showInsight?: boolean
  preferredVizType?: PracticeAgentVisualizationType | null
  mode?: 'workspace' | 'dashboard'
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [page, setPage] = useState(1)
  const [sortState, setSortState] = useState<{ column: string; direction: 'asc' | 'desc' } | null>(null)
  const [pendingSortState, setPendingSortState] = useState<{ column: string | null; direction: 'asc' | 'desc' } | null>(null)
  const sortDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [groupByDimension, setGroupByDimension] = useState<string | null>(null)
  const [vizOverride, setVizOverride] = useState<PracticeAgentVisualizationType | null>(null)
  // Dimension picker state: set when user clicks a data point, cleared after confirm/cancel
  const [pickerState, setPickerState] = useState<{ field: string; value: string } | null>(null)
  // Drilldown result: set after executing the true drilldown query
  const [drilldownResult, setDrilldownResult] = useState<PracticeAgentDrilldownData | null>(null)
  // Filter applied for the current drilldown (field + value used to filter before breakdown)
  const [drilldownFilter, setDrilldownFilter] = useState<{ field: string; value: string } | null>(null)
  // Drilldown pagination state
  const [drilldownPage, setDrilldownPage] = useState(1)
  const [activeDrilldownParams, setActiveDrilldownParams] = useState<{ field: string; value: string; dimension: string } | null>(null)
  // Stable key derived from the actual filter values — used as the useEffect dep instead of the
  // object reference, so that the auto-execute effect re-runs on Strict Mode remount (which
  // creates a fresh useMutation observer) rather than being skipped by a ref guard.
  const initialDrilldownKey = initialDrilldown
    ? `${JSON.stringify(initialDrilldown.filters)}|${initialDrilldown.dimension ?? ''}`
    : null

  // Workspace init — only for standalone single-report mode (not a group sub-panel)
  const { data: workspaceInitData, isLoading: initLoading, isError: initError } = usePracticeAgentWorkspaceInit(
    clientId,
    reportBuilderId,
    undefined,
    {},
    { enabled: !groupReportData },
  )

  // Page 2+, sorted, or group-by-changed data — calls reportBuilder/data with page/sort/groupBy params
  const usePaginated = page > 1 || sortState !== null || groupByDimension !== null
  const { data: paginatedData, isFetching: paginatedLoading } = usePracticeAgentReportData(
    clientId,
    reportBuilderId,
    {},
    reportBuilderGroupId ?? undefined,
    { enabled: usePaginated },
    page,
    sortState?.column,
    sortState?.direction,
    groupByDimension ?? undefined,
  )

  // Per-report insight — always fetched for every panel (standalone and group sub-panels).
  const { data: workspaceInsightData, isLoading: insightQueryLoading } = usePracticeAgentWorkspaceInsight(
    clientId,
    reportBuilderId,
    {},
  )

  // Page 1: use cached workspace/group data; page 2+, sorted, or group-by-changed: use paginated fetch
  const page1Data = groupReportData ?? workspaceInitData?.reportData
  const data = usePaginated ? (paginatedData ?? page1Data) : page1Data
  const isLoading = usePaginated ? paginatedLoading : (groupReportData ? false : initLoading)
  const isError = groupReportData ? false : initError
  const insightData = workspaceInsightData
  const insightLoading = insightQueryLoading

  const { data: reportBuilderOptions } = useReportBuilderOptions(clientId, data?.platformType ?? 0)

  const nameToValue = useMemo(() => {
    const allOptions = [
      ...(reportBuilderOptions?.fieldDimensionOptions ?? []),
      ...(reportBuilderOptions?.fieldMeasureOptions ?? []),
    ]
    return new Map(allOptions.map(o => [o.name, o.value]))
  }, [reportBuilderOptions])

  const sortColumnOptions = useMemo(
    () => (data?.columns ?? []).map(c => ({ value: nameToValue.get(c) ?? c, label: formatFieldDisplayName(c) })),
    [data?.columns, nameToValue],
  )

  // Derive sort display values — pending (immediate) › confirmed sort › backend default (first measure, desc)
  const { measureCols: sortMeasureCols } = useMemo(
    () => classifyColumns(data?.columns ?? [], data?.rows ?? []),
    [data?.columns, data?.rows],
  )
  const rawDefaultSortColumn = data?.sortColumn ?? sortMeasureCols[0] ?? data?.columns[0] ?? null
  const defaultSortColumn = rawDefaultSortColumn ? (nameToValue.get(rawDefaultSortColumn) ?? rawDefaultSortColumn) : null
  const displaySortColumn = pendingSortState?.column ?? sortState?.column ?? defaultSortColumn
  const displaySortDirection: 'asc' | 'desc' =
    pendingSortState?.direction ?? sortState?.direction ?? (data?.sortDirection as 'asc' | 'desc') ?? 'desc'

  // Debounce sort changes: update display immediately, fire API after 1 s
  function handleSortChange(col: string | null, dir: 'asc' | 'desc') {
    setPendingSortState({ column: col, direction: dir })
    if (sortDebounceRef.current) clearTimeout(sortDebounceRef.current)
    sortDebounceRef.current = setTimeout(() => {
      setSortState(col !== null ? { column: col, direction: dir } : null)
      setPage(1)
      setPendingSortState(null)
    }, 1000)
  }

  // Cleanup debounce timer on unmount
  useEffect(() => () => { if (sortDebounceRef.current) clearTimeout(sortDebounceRef.current) }, [])

  // Group-by change: refetch with the new dimension, preserving current page and sort
  function handleGroupByChange(dim: string) {
    setGroupByDimension(dim)
  }

  const executeDrilldownMutation = usePracticeAgentExecuteDrilldownMutation()

  function handleDataPointSelect(field: string, value: string) {
    if (!data || !field) return
    setPickerState({ field, value })
  }

  function handlePickerConfirm(drilldownDimension: string) {
    if (!pickerState) return
    // Execute true drilldown — result is consumed by the useEffect watching isSuccess
    setDrilldownPage(1)
    setActiveDrilldownParams({ field: pickerState.field, value: pickerState.value, dimension: drilldownDimension })
    executeDrilldownMutation.mutate(
      { reportBuilderId, field: pickerState.field, value: pickerState.value, dimension: drilldownDimension, page: 1, reportBuilderGroupId: reportBuilderGroupId ?? undefined },
    )
  }

  function handleDrilldownPageChange(p: number) {
    if (!activeDrilldownParams) return
    setDrilldownPage(p)
    executeDrilldownMutation.mutate({ reportBuilderId, ...activeDrilldownParams, page: p, reportBuilderGroupId: reportBuilderGroupId ?? undefined })
  }

  function clearDrilldown() {
    setDrilldownResult(null)
    setPickerState(null)
    setDrilldownFilter(null)
    setDrilldownPage(1)
    setActiveDrilldownParams(null)
    setPage(1)
    navigate(location.pathname, { replace: true })
  }

  const effectiveViz: PracticeAgentVisualizationType = vizOverride ?? preferredVizType ?? data?.visualizationType ?? 'Table'
  const isDashboardMode = mode === 'dashboard'

  // Convert drilldown result to a shape compatible with ReportVisualization
  const drilldownAsReportData: PracticeAgentReportData | null = drilldownResult
    ? {
        reportBuilderId,
        title: '',
        visualizationType: effectiveViz,
        columns: drilldownResult.columns,
        rows: drilldownResult.rows,
        chartData: drilldownResult.chartData,
        kpiCards: drilldownResult.kpiCards,
        appliedFilters: {},
        retrievedAt: new Date().toISOString(),
        platformType: data?.platformType ?? 0,
        ...(drilldownResult.totalRows != null ? { totalRows: drilldownResult.totalRows } : {}),
        ...(drilldownResult.page      != null ? { page:      drilldownResult.page      } : {}),
        ...(drilldownResult.pageSize  != null ? { pageSize:  drilldownResult.pageSize  } : {}),
      }
    : null

  // Clear stale drilldown result when navigating to a different history entry
  useEffect(() => {
    if (!initialDrilldownKey) return
    setDrilldownResult(null)
  }, [initialDrilldownKey])

  // When dimension is unknown, open picker pre-filled with filter so user can choose
  useEffect(() => {
    if (!initialDrilldown || !initialDrilldownKey || initialDrilldown.dimension) return
    const [[field, value] = []] = Object.entries(initialDrilldown.filters)
    if (!field || !value) return
    setPickerState({ field, value })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDrilldownKey])

  // Auto-execute drilldown when re-opening from history with a known dimension
  useEffect(() => {
    if (!initialDrilldown?.dimension || !initialDrilldownKey) return
    const [[field, value] = []] = Object.entries(initialDrilldown.filters)
    if (!field || !value) return
    setDrilldownPage(1)
    setActiveDrilldownParams({ field, value, dimension: initialDrilldown.dimension })
    setDrilldownFilter({ field, value })
    executeDrilldownMutation.mutate({
      reportBuilderId,
      field,
      value,
      dimension: initialDrilldown.dimension,
      page: 1,
      reportBuilderGroupId: reportBuilderGroupId ?? undefined,
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDrilldownKey])

  // Consume drilldown mutation result as soon as it succeeds — avoids dropped inline callbacks
  useEffect(() => {
    if (!executeDrilldownMutation.isSuccess || !executeDrilldownMutation.data) return
    const incoming = executeDrilldownMutation.data
    if (drilldownPage > 1) {
      // Preserve the page-1 insight when paginating
      setDrilldownResult(prev => ({ ...incoming, insight: prev?.insight ?? '' }))
    } else {
      setDrilldownResult(incoming)
      if (pickerState) setDrilldownFilter({ field: pickerState.field, value: pickerState.value })
      setPickerState(null)
    }
    executeDrilldownMutation.reset()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executeDrilldownMutation.isSuccess])

  return (
    <div
      className={`overflow-hidden flex flex-col ${
        isDashboardMode
          ? 'relative rounded-[28px] border border-brand-100 bg-white/95 shadow-card backdrop-blur'
          : 'bg-white border border-gray-200 rounded-xl shadow-card'
      }`}
      data-export-report="true"
      data-export-title={data?.title ?? 'Report'}
      data-viz-type={effectiveViz}
      data-report-builder-id={reportBuilderId}
      data-export-sort-column={sortState?.column ?? ''}
      data-export-sort-direction={sortState?.direction ?? ''}
      data-export-group-by={groupByDimension ?? ''}
      data-export-drilldown-field={activeDrilldownParams?.field ?? ''}
      data-export-drilldown-value={activeDrilldownParams?.value ?? ''}
      data-export-drilldown-dimension={activeDrilldownParams?.dimension ?? ''}
    >
      {/* Panel header — title left, viz switcher right */}
      <div className={`flex items-center justify-between border-b ${isDashboardMode ? 'border-brand-100/70 px-6 py-5' : 'border-gray-100 px-4 py-3'}`}>
        <h3 className={`${isDashboardMode ? 'text-base font-semibold text-slate-950' : 'text-sm font-semibold text-gray-900'} truncate mr-2`}>
          {data?.title ?? (isLoading ? '…' : 'Report')}
        </h3>
        {data != null && !isDashboardMode && (
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {VIZ_SWITCHER.map(({ key, icon, label }) => (
              <Tooltip key={key} title={label}>
                <Button
                  size="small"
                  type="text"
                  icon={icon}
                  onClick={() => setVizOverride(key)}
                  className={`transition-colors ${effectiveViz === key ? 'feature-soft-text feature-soft-surface' : 'text-gray-400 hover:text-gray-600'}`}
                  aria-label={`Switch to ${label} view`}
                />
              </Tooltip>
            ))}
          </div>
        )}
      </div>

      {/* Drilldown breadcrumb */}
      {drilldownResult && drilldownFilter && pickerState === null && (
        <div className={`feature-panel-soft border-b flex items-center justify-between gap-3 ${isDashboardMode ? 'px-6 py-3' : 'px-4 py-2'}`}>
          <div className="feature-label-accent flex items-center gap-1.5 text-xs min-w-0">
            <span
              className="feature-crumb-link cursor-pointer underline-offset-2 hover:underline"
              onClick={clearDrilldown}
            >
              {data?.title ?? 'All'}
            </span>
            {/* {drilldownFilter && (
              <> */}
                <span className="text-gray-400">›</span>
                <span className="text-gray-500">{formatFieldDisplayName(drilldownFilter.field)}: {drilldownFilter.value}</span>
              {/* </>
            )} */}
            {/* <span className="text-gray-400">›</span>
            <span className="font-medium truncate">
              {drilldownResult.columns[0] ?? 'Breakdown'}
            </span> */}
          </div>
          <Button size="small" type="text" onClick={clearDrilldown} className="feature-text-action flex-shrink-0 text-xs">
            Clear
          </Button>
        </div>
      )}

      {/* Sort controls (hidden while drilled down, always rendered regardless of viz loading state) */}
      {!drilldownResult && data && !isDashboardMode && (
        <div className="px-4 pt-3">
          <SortControls
            columns={data.columns}
            rows={data.rows}
            vizType={effectiveViz}
            sortColumn={displaySortColumn}
            sortDirection={displaySortDirection}
            onChange={handleSortChange}
            sortOptions={sortColumnOptions}
          />
        </div>
      )}

      {/* Main visualization or drilldown result */}
      <div className={`flex-1 ${isDashboardMode ? 'px-6 py-6' : 'px-4 py-4'}`}>
        {isLoading ? (
          <div className="flex justify-center items-center h-40"><Spin /></div>
        ) : isError ? (
          <Alert message="Failed to load report data" type="error" showIcon />
        ) : drilldownResult && drilldownAsReportData ? (
          <>
            {executeDrilldownMutation.isPending ? (
              <div className="flex justify-center items-center h-40"><Spin /></div>
            ) : (
              <ReportVisualization data={drilldownAsReportData} vizType={effectiveViz} isGroup={!!groupReportData} onPageChange={handleDrilldownPageChange} mode={mode} />
            )}
            {drilldownResult.insight && (
              <div className="feature-panel-soft mt-4 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <RobotOutlined className="feature-icon-accent" />
                  <span className="feature-label-accent text-xs font-semibold uppercase tracking-wide">PracIntel Drilldown Insight</span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{drilldownResult.insight}</p>
              </div>
            )}
          </>
        ) : data ? (
          <>
            {!drilldownResult && !isDashboardMode && (
              <p className="text-xs text-gray-400 mb-2">Click any data point to drill down</p>
            )}
            {executeDrilldownMutation.isPending ? (
              <div className="flex justify-center items-center h-40"><Spin tip="Loading drilldown…" /></div>
            ) : (
              <ReportVisualization
                data={data}
                vizType={effectiveViz}
                onSelect={handleDataPointSelect}
                onPageChange={setPage}
                onGroupByChange={handleGroupByChange}
                isGroup={!!groupReportData}
                sortColumn={displaySortColumn}
                mode={mode}
              />
            )}
          </>
        ) : null}
      </div>

      {/* Insight (hidden while drilled down since drilldown shows its own) */}
      {!drilldownResult && showInsight && (
        <div className={isDashboardMode ? 'px-6 pb-6' : 'px-4 pb-4'}>
          <InsightCard
            insight={insightData?.insight}
            isLoading={insightLoading || executeDrilldownMutation.isPending}
          />
        </div>
      )}

      {/* Dimension picker modal */}
      <DimensionPickerModal
        open={!!pickerState}
        dimensions={drilldownDimensions ?? []}
        defaultDimension={defaultDrilldownDimension ?? null}
        onConfirm={handlePickerConfirm}
        onCancel={() => setPickerState(null)}
        isLoading={executeDrilldownMutation.isPending}
      />
    </div>
  )
}

// Re-export PaginationBar to satisfy any internal uses (not needed externally)
export { PaginationBar }
